import { useMutation } from '@apollo/client/react';
import { get } from 'lodash';
import type { FC, ReactNode } from 'react';

import { Headline, Icon, Popover } from '@zen/DesignSystem';
import { useNotification } from '@zen/utils/hooks/useNotification';
import { performMutation } from '@zen/utils/performMutation';

import {
  AcceptSuggestionDocument,
  type AcceptSuggestionMutationResult,
  type AcceptSuggestionMutationVariables,
  RejectSuggestionDocument,
  type RejectSuggestionMutationResult,
  type RejectSuggestionMutationVariables
} from './graphql';
import { enumToSentenceCase } from './helpers';
import Suggestion from './Suggestion';
import type { Suggestion as SuggestionType } from './types';

interface Props {
  originalValuePresent: boolean;
  refetchQueries: string[];
  suggestions: SuggestionType[];
}

const Suggestions: FC<Props> = ({ originalValuePresent, refetchQueries, suggestions }) => {
  const { addError, addSuccess } = useNotification();

  const [acceptSuggestion] = useMutation<AcceptSuggestionMutationResult, AcceptSuggestionMutationVariables>(
    AcceptSuggestionDocument
  );

  const [rejectSuggestion] = useMutation<RejectSuggestionMutationResult, RejectSuggestionMutationVariables>(
    RejectSuggestionDocument
  );

  const handleAcceptSuggestion = async (suggestionId: string) => {
    await performMutation({
      mutationFn: () =>
        acceptSuggestion({
          awaitRefetchQueries: true,
          refetchQueries: [...refetchQueries, 'getSuggestions'],
          variables: {
            suggestionId
          }
        }),
      onError: (errors) => {
        addError(get(errors, '[0].message', 'Suggestion could not be accepted.'));
      },
      onSuccess: () => {
        addSuccess(`Suggestion accepted`);
      }
    });
  };

  const handleRejectSuggestion = async (suggestionId: string) => {
    await performMutation({
      mutationFn: () =>
        rejectSuggestion({
          awaitRefetchQueries: true,
          refetchQueries: [...refetchQueries, 'getSuggestions'],
          variables: {
            input: {
              rejectReason: 'Rejected',
              suggestionId
            }
          }
        }),
      onError: (errors) => {
        addError(get(errors, '[0].message', 'Suggestion could not be rejected.'));
      },
      onSuccess: () => {
        addSuccess(`Suggestion rejected`);
      }
    });
  };

  const renderSuggestions = ({ close }: { close: () => void }): ReactNode => {
    return (
      <div className="bg-sunset-base p-0.5 rounded-md">
        <div className=" bg-white rounded-sm flex flex-col gap-2 p-4">
          <Headline className="mb-3" level={3}>
            {enumToSentenceCase(suggestions[0].targetAttribute)}
          </Headline>

          {suggestions.map((suggestion) => {
            return (
              <Suggestion
                key={suggestion.id}
                id={suggestion.id}
                onAccept={handleAcceptSuggestion}
                onReject={handleRejectSuggestion}
                proposedValue={suggestion.proposedValue as string}
                sources={suggestion.sources}
                targetAttribute={suggestion.targetAttribute}
                variant={'popover'}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const renderSuggestionPopover = () => {
    return (
      <Popover showArrow={true} popover={renderSuggestions} renderInPortal={true} placement="top" popoverClassNames="min-w-150">
        <div className="bg-sunrise-light rounded-4xl flex items-center px-2 gap-2 justify-between w-auto h-8 text-navy-dark mt-2 cursor-pointer">
          <div className="flex items-center gap-2 font-bold">
            <Icon icon="zicon-sparkles-empty" />
            {suggestions?.length} suggestions
          </div>
          <div className="flex items-center gap-2"></div>
        </div>
      </Popover>
    );
  };

  if (suggestions.length > 1) {
    return renderSuggestionPopover();
  }

  if (suggestions.length === 1) {
    const suggestion = suggestions[0];

    return (
      <Suggestion
        id={suggestion.id}
        onAccept={handleAcceptSuggestion}
        onReject={handleRejectSuggestion}
        proposedValue={suggestion.proposedValue as string}
        sources={suggestion.sources}
        targetAttribute={suggestion.targetAttribute}
        variant={originalValuePresent ? 'pill' : 'text'}
      />
    );
  }
};

export default Suggestions;
